import { useState, useCallback, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, PiggyBank, Sparkles, Save } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { defaultState, getTermMonths, calculate } from "@/lib/budget";
import { StepIncome, StepLiving, StepEssentials, StepSocial, StepOneOffs } from "@/components/Steps";
import Dashboard from "@/components/Dashboard";

const API = process.env.REACT_APP_BACKEND_URL || "";

const getSessionId = () => {
  let id = localStorage.getItem("sbp_session_id");
  if (!id) { id = crypto.randomUUID(); localStorage.setItem("sbp_session_id", id); }
  return id;
};

const STORAGE_KEY = "sbp_state_v1";
const SECTIONS = ["income", "term", "living", "bills", "food", "transport", "study", "health", "social", "oneOffs"];

const loadState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState();
    const saved = JSON.parse(raw);
    const d = defaultState();
    const merged = { ...d };
    SECTIONS.forEach((s) => {
      if (saved[s] && typeof saved[s] === "object") merged[s] = { ...d[s], ...saved[s] };
    });
    return merged;
  } catch {
    return defaultState();
  }
};

// True only when the student has entered some real figures (not just defaults)
const hasBudgetData = (s) => {
  if (!s) return false;
  if (s.living?.rent) return true;
  for (const sec of ["bills", "food", "transport", "study", "health", "social"]) {
    for (const v of Object.values(s[sec] || {})) if (v) return true;
  }
  const inc = s.income || {};
  for (const [k, v] of Object.entries(inc)) {
    if (k === "schedule" || k === "payments") continue;
    if (v) return true;
  }
  for (const p of inc.payments || []) if (p.amount) return true;
  for (const o of Object.values(s.oneOffs || {})) if (o.amount) return true;
  return false;
};

const STEPS = [
  { title: "Let's work out your student budget", intro: "Tell us what money you'll receive this academic year. We'll spread it across the months you're studying so you know what you can safely spend." },
  { title: "Where you live", intro: "Rent is usually a student's biggest cost — let's get it right." },
  { title: "Monthly essentials", intro: "The regular stuff you pay for every month. Skip anything that doesn't apply." },
  { title: "Social life", intro: "The fun budget. Be honest — it helps the numbers stay realistic." },
  { title: "One-off costs", intro: "The things students forget. We'll spread each across the year automatically." },
];

export default function BudgetPlanner() {
  const [view, setView] = useState("landing"); // landing | wizard | dashboard
  const [step, setStep] = useState(0);
  const [state, setState] = useState(loadState);
  const [restored, setRestored] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? hasBudgetData(JSON.parse(raw)) : false;
    } catch {
      return false;
    }
  });
  const budgetIdRef = useRef(null);

  // Auto-save to localStorage
  useEffect(() => {
    try {
      if (hasBudgetData(state)) localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      else localStorage.removeItem(STORAGE_KEY);
    } catch { /* storage unavailable */ }
  }, [state]);

  // Welcome-back note if a saved budget was found
  useEffect(() => {
    if (restored) toast.success("Welcome back — we restored your saved budget on this device.");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveBudgetToApi = useCallback(async (currentState) => {
    try {
      const results = calculate(currentState);
      const payload = { sessionId: getSessionId(), state: currentState, results };
      if (budgetIdRef.current) {
        await axios.put(`${API}/api/budgets/${budgetIdRef.current}`, payload);
      } else {
        const { data } = await axios.post(`${API}/api/budgets`, payload);
        budgetIdRef.current = data.id;
      }
    } catch {
      // silent — localStorage is the fallback
    }
  }, []);

  const months = getTermMonths(state.term);

  const setSection = useCallback((section, key, value) => {
    setState((s) => ({ ...s, [section]: { ...s[section], [key]: value } }));
  }, []);
  const setIncome = useCallback((k, v) => setSection("income", k, v), [setSection]);
  const setLiving = useCallback((k, v) => setSection("living", k, v), [setSection]);
  const setTerm = useCallback((k, v) => setSection("term", k, v), [setSection]);
  const setPayment = useCallback((idx, field, value) => {
    setState((s) => {
      const payments = s.income.payments.map((p, i) => (i === idx ? { ...p, [field]: value } : p));
      return { ...s, income: { ...s.income, payments } };
    });
  }, []);
  const setOneOff = useCallback((k, field, value) => {
    setState((s) => ({ ...s, oneOffs: { ...s.oneOffs, [k]: { ...s.oneOffs[k], [field]: value } } }));
  }, []);

  const reset = async () => {
    try {
      if (budgetIdRef.current) await axios.delete(`${API}/api/budgets/${budgetIdRef.current}`);
    } catch { /* silent */ }
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
    budgetIdRef.current = null;
    setState(defaultState());
    setRestored(false);
    setStep(0);
    setView("landing");
    toast("Cleared — starting a fresh budget.");
  };

  const validateStep = (s) => {
    if (s === 0) {
      const inc = state.income;
      const hasIncome =
        inc.maintenanceLoan || inc.maintenanceGrant || inc.bursary ||
        inc.scholarships || inc.familySupport || inc.partTime ||
        inc.savings || inc.other ||
        (inc.schedule === "3" && inc.payments.some((p) => p.amount));
      if (!hasIncome) { toast.error("Please enter at least one income source before continuing."); return false; }
    }
    if (s === 1) {
      if (!state.living.rent) { toast.error("Please enter your monthly rent before continuing."); return false; }
    }
    return true;
  };

  const stepProps = { state, setSection, setIncome, setLiving, setTerm, setPayment, setOneOff, months };
  const STEP_VIEWS = [
    <StepIncome {...stepProps} />,
    <StepLiving {...stepProps} />,
    <StepEssentials {...stepProps} />,
    <StepSocial {...stepProps} />,
    <StepOneOffs {...stepProps} />,
  ];

  if (view === "dashboard") {
    return <Dashboard state={state} onEdit={() => { setStep(0); setView("wizard"); }} onReset={reset} />;
  }

  if (view === "landing") {
    return (
      <div className="mx-auto grid min-h-screen max-w-6xl items-center gap-10 px-4 py-12 sm:px-6 lg:grid-cols-2">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="inline-flex items-center gap-2 rounded-full bg-[#EDEBFF] px-3 py-1 text-sm font-medium text-[#734FFC]">
            <Sparkles size={14} /> Free student budget planner
          </span>
          <h1 className="mt-5 font-display text-4xl font-bold tracking-tight text-[#1A1A1A] sm:text-5xl" aria-label="Know exactly what you can spend this term.">
            Know exactly what you can
            <span className="relative mx-2.5 inline-block">
              spend
              <span className="absolute -bottom-0.5 left-0 h-[6px] w-full rounded-full bg-[#FF8700]" />
            </span>
            this term.
          </h1>
          <p className="mt-4 max-w-md text-lg text-[#747D89]">
            Add your loan, rent and everyday costs. We'll spread it across your academic year and show you a safe weekly spend — so you don't run out before your next payment.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            {restored ? (
              <>
                <Button data-testid="continue-btn" onClick={() => setView("dashboard")} className="rounded-full bg-[#0F64F8] px-6 py-6 text-base text-white hover:bg-[#134192]">
                  Continue my budget <ArrowRight size={18} className="ml-2" />
                </Button>
                <Button variant="outline" data-testid="edit-answers-btn" onClick={() => { setStep(0); setView("wizard"); }} className="rounded-full border-[#E8EAEF] px-6 py-6 text-base">
                  Edit my answers
                </Button>
              </>
            ) : (
              <Button data-testid="start-btn" onClick={() => { setStep(0); setView("wizard"); }} className="rounded-full bg-[#0F64F8] px-6 py-6 text-base text-white hover:bg-[#134192]">
                Start my budget <ArrowRight size={18} className="ml-2" />
              </Button>
            )}
          </div>
          <p className="mt-4 flex items-center gap-1.5 text-sm text-[#747D89]">
            <Save size={14} /> No sign-up required.
          </p>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.1 }} className="relative">
          <img
            src="https://images.pexels.com/photos/9052857/pexels-photo-9052857.jpeg"
            alt="Student planning a budget"
            className="w-full rounded-3xl border border-[#E8EAEF] object-cover shadow-[0_20px_60px_rgba(31,41,36,0.12)]"
          />
          <div className="absolute -bottom-5 left-5 flex items-center gap-3 rounded-2xl border border-[#E8EAEF] bg-white px-4 py-3 shadow-lg">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#EDEBFF] text-[#734FFC]"><PiggyBank size={20} /></span>
            <div>
              <p className="text-xs text-[#747D89]">Safe to spend</p>
              <p className="font-display text-lg font-bold text-[#377D30]">£214 / week</p>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Wizard
  const meta = STEPS[step];
  const isLast = step === STEPS.length - 1;
  const next = () => {
    if (!validateStep(step)) return;
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (isLast) { saveBudgetToApi(state); setView("dashboard"); }
    else setStep((s) => s + 1);
  };
  const back = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    step === 0 ? setView("landing") : setStep((s) => s - 1);
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-12">
      <div className="mb-6">
        <div className="mb-2 flex items-center justify-between text-sm text-[#747D89]">
          <span className="font-medium">Step {step + 1} of {STEPS.length}</span>
          <span>{Math.round(((step + 1) / STEPS.length) * 100)}%</span>
        </div>
        <Progress value={((step + 1) / STEPS.length) * 100} className="h-2 bg-[#E8EAEF]" data-testid="wizard-progress" />
      </div>

      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold tracking-tight text-[#1A1A1A] sm:text-4xl">{meta.title}</h1>
        <p className="mt-2 text-[#747D89]">{meta.intro}</p>
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={step}>{STEP_VIEWS[step]}</motion.div>
      </AnimatePresence>

      <div className="mt-8 flex items-center justify-between">
        <Button variant="ghost" data-testid="wizard-back-btn" onClick={back} className="rounded-full text-[#747D89] hover:text-[#1A1A1A]">
          <ArrowLeft size={18} className="mr-2" /> Back
        </Button>
        <Button data-testid="wizard-next-btn" onClick={next} className="rounded-full bg-[#0F64F8] px-6 text-white hover:bg-[#134192]">
          {isLast ? (<><Check size={18} className="mr-2" /> See my budget</>) : (<>Continue <ArrowRight size={18} className="ml-2" /></>)}
        </Button>
      </div>
    </div>
  );
}
